import type { Notification, NotificationEventKind } from '@/src/store/apps/resto365Notifications'
import { getNotificationSoundEnabled } from '@/src/@core/lib/notificationSoundPreference'

const SOCKET_EVENT_KINDS = new Set<NotificationEventKind>([
  'ingredient_auto_reenable',
  'ingredient_pre_expiry_reminder',
  'equipment_permanent_disable_decision',
  'equipment_auto_reenable'
])

type SocketPayload = {
  id?: string | number
  message?: string
  category?: Notification['category']
  time?: string | Date
  eventKind?: NotificationEventKind
  userId?: number
}

export function notificationFromSocketPayload(raw: unknown): Notification {
  const p = raw as SocketPayload
  const createdAt =
    p.time != null
      ? typeof p.time === 'string'
        ? new Date(p.time)
        : p.time instanceof Date
          ? p.time
          : new Date()
      : new Date()

  const category: Notification['category'] = p.category === 'restaurant' || p.category === 'menu' ? p.category : 'menu'

  const id: string | undefined = typeof p.id === 'string' ? p.id : typeof p.id === 'number' ? String(p.id) : undefined

  return {
    ...(id != null ? { id } : {}),
    ...(typeof p.userId === 'number' ? { userId: p.userId } : {}),
    message: typeof p.message === 'string' ? p.message : '',
    category,
    createdAt,
    eventKind: p.eventKind && SOCKET_EVENT_KINDS.has(p.eventKind) ? p.eventKind : undefined,
    read: false
  }
}

export function playNotificationBell(): void {
  if (!getNotificationSoundEnabled()) return
  try {
    const AC =
      window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AC) return
    const ctx = new AC()
    const ring = (freq: number, start: number, dur: number) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.setValueAtTime(freq, start)
      osc.connect(gain)
      gain.connect(ctx.destination)
      gain.gain.setValueAtTime(0.0001, start)
      gain.gain.exponentialRampToValueAtTime(0.12, start + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, start + dur)
      osc.start(start)
      osc.stop(start + dur + 0.02)
    }
    ring(880, ctx.currentTime, 0.22)
    ring(1108, ctx.currentTime + 0.18, 0.28)
    void ctx.resume().catch(() => undefined)
    window.setTimeout(() => {
      void ctx.close()
    }, 900)
  } catch {
    // Missing AudioContext, autoplay policy, etc.
  }
}

/** Sound + speech bubble on the notifications bell for any realtime notification. */
export function onResto365SocketNotificationDelivered(message: string): void {
  playNotificationBell()
  const text = message.trim() || 'You have a new notification.'
  window.dispatchEvent(
    new CustomEvent('bhyve-bell-highlight', {
      detail: {
        message: text,
        durationMs: 12_000
      }
    })
  )
}
